import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Plane, AlertCircle, RefreshCw, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { servicesAPI } from '@/services/api';

// Service illustration icons from assets
import iconProfileAssessment from '../../assets/Icon/Profile Assessment.png';
import iconPreapplication from '../../assets/Icon/Preapplication Support.png';
import iconApplyUniversity from '../../assets/Icon/Apply University.png';
import iconVisaInterview from '../../assets/Icon/Visa & Interview Support.png';
import iconTicketTravel from '../../assets/Icon/Ticket & Travel Support.png';
import iconAccommodation from '../../assets/Icon/Find Accommodation.png';
import iconEducationLoan from '../../assets/Icon/loan1.png';
import iconFindJobs from '../../assets/Icon/Find Jobs Abroad.png';
import iconSuccessful from '../../assets/Icon/sucessful.png';

const SERVICE_ICON_MAP = {
  'Profile Assessment': iconProfileAssessment,
  'PreApplication Support': iconPreapplication,
  'Apply University': iconApplyUniversity,
  'Visa & Interview': iconVisaInterview,
  'Ticket & Travel': iconTicketTravel,
  Accommodation: iconAccommodation,
  'Education Loan': iconEducationLoan,
  'Find Jobs Abroad': iconFindJobs,
};

const SERVICE_ICON_FALLBACK = [
  iconProfileAssessment,
  iconPreapplication,
  iconApplyUniversity,
  iconVisaInterview,
  iconTicketTravel,
  iconAccommodation,
  iconEducationLoan,
  iconFindJobs,
];

const getServiceIcon = (service, index) =>
  SERVICE_ICON_MAP[service.name] || SERVICE_ICON_FALLBACK[index];

const FALLBACK_SERVICES = [
  { serviceId: 'service-1', name: 'Profile Assessment', icon: 'Target' },
  { serviceId: 'service-2', name: 'PreApplication Support', icon: 'FileText' },
  { serviceId: 'service-3', name: 'Apply University', icon: 'GraduationCap' },
  { serviceId: 'service-4', name: 'Visa & Interview', icon: 'Stamp' },
  { serviceId: 'service-5', name: 'Ticket & Travel', icon: 'Plane' },
  { serviceId: 'service-6', name: 'Accommodation', icon: 'Home' },
  { serviceId: 'service-7', name: 'Education Loan', icon: 'Banknote' },
  { serviceId: 'service-8', name: 'Find Jobs Abroad', icon: 'Briefcase' },
];

const SERVICE_ROUTES_BY_NAME = {
  'Profile Assessment': '/dashboard/services/profile-assessment',
  'PreApplication Support': '/dashboard/services/preapplication-support',
  'Apply University': '/dashboard/services/apply-university',
  'Visa & Interview': '/dashboard/services/visa-interview',
  'Ticket & Travel': '/dashboard/services/ticket-travel',
  Accommodation: '/dashboard/services/accommodation',
  'Education Loan': '/dashboard/services/education-loan',
  'Find Jobs Abroad': '/dashboard/services/jobs-abroad',
};

const SERVICE_ROUTE_FALLBACK = [
  '/dashboard/services/profile-assessment',
  '/dashboard/services/preapplication-support',
  '/dashboard/services/apply-university',
  '/dashboard/services/visa-interview',
  '/dashboard/services/ticket-travel',
  '/dashboard/services/accommodation',
  '/dashboard/services/education-loan',
  '/dashboard/services/jobs-abroad',
];

const getServiceRoute = (service, index) =>
  SERVICE_ROUTES_BY_NAME[service.name] ||
  SERVICE_ROUTE_FALLBACK[index] ||
  '/dashboard/services';

// ── Shared card header ────────────────────────────────────────────────────────
function TimelineHeader({ stepCount }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-white shadow-md shadow-[#1C8DEE]/15 flex items-center justify-center shrink-0">
          <Plane className="w-5 h-5 text-[#1C8DEE]" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] font-bold text-slate-900 leading-tight truncate">
            Study Abroad Journey
          </h3>
          <p className="text-[12px] text-[#1C8DEE]/70 font-medium mt-0.5 leading-none">
            Your step-by-step path to studying abroad
          </p>
        </div>
      </div>
      {stepCount != null && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-sm border border-white/80 shrink-0 ml-3">
          <span className="w-1.5 h-1.5 rounded-full bg-[#1C8DEE] shrink-0" />
          <span className="text-[11px] font-bold text-[#1C8DEE] whitespace-nowrap">
            {stepCount} steps
          </span>
        </div>
      )}
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────
function TimelineSkeleton() {
  return (
    <Card className="p-6 rounded-2xl border-0 bg-[#D8EFFF] shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-white/70 animate-pulse shrink-0" />
          <div className="space-y-2 flex-1">
            <div className="w-36 h-3.5 rounded-md bg-white/70 animate-pulse" />
            <div className="w-48 h-2.5 rounded-md bg-white/50 animate-pulse" />
          </div>
        </div>
        <div className="w-20 h-7 rounded-full bg-white/70 animate-pulse shrink-0 ml-3" />
      </div>

      {/* Mobile skeleton */}
      <div className="flex flex-col gap-3 md:hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-white/60 animate-pulse shrink-0" />
            <div className="w-11 h-11 rounded-2xl bg-white/70 animate-pulse shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 rounded-md bg-white/70 animate-pulse w-3/4" />
              <div className="h-2.5 rounded-md bg-white/50 animate-pulse w-1/2" />
            </div>
            <div className="w-16 h-7 rounded-full bg-white/60 animate-pulse shrink-0" />
          </div>
        ))}
      </div>

      {/* Desktop skeleton */}
      <div className="hidden md:block overflow-hidden">
        <div
          className="grid gap-1"
          style={{ gridTemplateColumns: 'repeat(9, minmax(72px, 1fr))' }}
        >
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-2.5">
              <div className="w-14 h-14 rounded-2xl bg-white/70 animate-pulse" />
              <div className="w-14 h-2.5 rounded-md bg-white/60 animate-pulse" />
              <div className="w-10 h-2 rounded-md bg-white/50 animate-pulse" />
              <div className="w-14 h-6 rounded-full bg-white/60 animate-pulse" />
            </div>
          ))}
        </div>
        <div className="mt-5 h-7 rounded-full bg-white/40 animate-pulse" />
      </div>
    </Card>
  );
}

// ── Error state ───────────────────────────────────────────────────────────────
function ErrorState({ message, onRetry }) {
  return (
    <Card className="py-12 px-6 sm:px-8 rounded-2xl border-0 bg-[#D8EFFF] shadow-sm">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center">
          <AlertCircle className="w-6 h-6 text-rose-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-800 mb-1">
            Unable to Load Journey
          </h3>
          <p className="text-xs text-slate-500 max-w-sm">{message}</p>
        </div>
        <button
          onClick={onRetry}
          className="flex items-center gap-2 px-4 py-2 bg-white shadow-sm hover:shadow-md text-slate-600 rounded-xl transition-all text-sm font-medium"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Try Again
        </button>
      </div>
    </Card>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <Card className="py-12 px-6 sm:px-8 rounded-2xl border-0 bg-[#D8EFFF] shadow-sm">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="w-12 h-12 rounded-2xl bg-white shadow-md shadow-[#1C8DEE]/15 flex items-center justify-center">
          <Plane className="w-6 h-6 text-[#1C8DEE]" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-800 mb-1">
            Start Your Journey
          </h3>
          <p className="text-xs text-slate-500">
            No services available at the moment.
          </p>
        </div>
      </div>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function StudentJourneyTimeline() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await servicesAPI.getAll();
      const serviceData = response.services || response.data || response;
      setServices(
        Array.isArray(serviceData) && serviceData.length > 0
          ? serviceData
          : FALLBACK_SERVICES,
      );
    } catch (err) {
      console.error('Failed to fetch journey data:', err);
      setError(
        err.response?.data?.message ||
          'Failed to load your journey. Please try again.',
      );
      setServices(FALLBACK_SERVICES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <TimelineSkeleton />;
  if (error && services.length === 0)
    return <ErrorState message={error} onRetry={fetchData} />;
  if (services.length === 0) return <EmptyState />;

  const totalCols = services.length + 1;
  const colTemplate = `repeat(${totalCols}, minmax(0, 1fr))`;
  const minWidth = `${totalCols * 76}px`;

  const renderServiceCell = (service, index, vertical = false) => {
    const serviceIcon = getServiceIcon(service, index);
    const route = getServiceRoute(service, index);

    if (vertical) {
      // ── Mobile row ──
      return (
        <motion.div
          key={service.serviceId}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.04, duration: 0.25, ease: 'easeOut' }}
          className="group flex items-center gap-3 py-2 rounded-xl hover:bg-white/30 transition-colors duration-150"
        >
          {/* Step dot */}
          <div className="w-7 h-7 rounded-full bg-white/70 border border-[#1C8DEE]/30 shadow-sm flex items-center justify-center shrink-0 z-10">
            <span className="text-[10px] font-bold text-[#1C8DEE]/70 tabular-nums leading-none select-none">
              {index + 1}
            </span>
          </div>

          {/* Icon */}
          <div className="w-11 h-11 rounded-xl bg-white shadow-[0_2px_8px_0_rgba(28,141,238,0.12)] flex items-center justify-center shrink-0 group-hover:shadow-[0_4px_14px_0_rgba(28,141,238,0.18)] group-hover:-translate-y-0.5 transition-all duration-200">
            <img
              src={serviceIcon}
              alt={service.name}
              className="w-7 h-7 object-contain group-hover:scale-105 transition-transform duration-200"
            />
          </div>

          {/* Name */}
          <p className="flex-1 min-w-0 text-sm font-semibold text-slate-800 truncate group-hover:text-slate-900 transition-colors duration-150">
            {service.name}
          </p>

          {/* Apply CTA */}
          <Link
            to={route}
            className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/70 border border-[#1C8DEE]/25 text-xs font-semibold text-[#1C8DEE]/80 hover:bg-white hover:border-[#1C8DEE]/50 hover:text-[#1C8DEE] transition-all duration-200"
          >
            Apply
            <ArrowRight className="w-3 h-3" />
          </Link>
        </motion.div>
      );
    }

    // ── Desktop / Tablet column ──
    return (
      <motion.div
        key={service.serviceId}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.04, duration: 0.25, ease: 'easeOut' }}
        className="group flex flex-col items-center gap-2 px-0.5"
      >
        {/* Icon container */}
        <div className="w-14 h-14 rounded-2xl bg-white shadow-[0_4px_12px_0_rgba(28,141,238,0.12)] flex items-center justify-center group-hover:shadow-[0_6px_20px_0_rgba(28,141,238,0.20)] group-hover:-translate-y-1 transition-all duration-200">
          <img
            src={serviceIcon}
            alt={service.name}
            className="w-9 h-9 object-contain group-hover:scale-105 transition-transform duration-200"
          />
        </div>

        {/* Name */}
        <p className="text-[11px] font-bold text-slate-700 text-center leading-snug px-0.5 h-[30px] flex items-center justify-center group-hover:text-slate-900 transition-colors duration-200 line-clamp-2">
          {service.name}
        </p>

        {/* Apply button */}
        <Link
          to={route}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/70 border border-[#1C8DEE]/25 text-[10px] font-semibold text-[#1C8DEE]/80 hover:bg-white hover:border-[#1C8DEE]/50 hover:text-[#1C8DEE] transition-all duration-200"
        >
          Apply
          <ArrowRight className="w-2.5 h-2.5 shrink-0 group-hover:translate-x-0.5 transition-transform duration-200" />
        </Link>
      </motion.div>
    );
  };

  return (
    <Card className="p-4 sm:p-6 rounded-2xl border-0 bg-[#D8EFFF] shadow-sm hover:shadow-md transition-shadow duration-300">
      <TimelineHeader stepCount={services.length} />

      {/* ════════════════════════════════════════
          MOBILE  (< md) — vertical timeline list
          ════════════════════════════════════════ */}
      <div className="md:hidden relative">
        {/* Vertical connector line */}
        <div
          className="absolute left-[13px] top-[14px] bottom-[14px] w-[2px] rounded-full"
          style={{
            background:
              'linear-gradient(to bottom, rgba(28,141,238,0.08), rgba(28,141,238,0.20), rgba(28,141,238,0.08))',
          }}
        />

        <div className="flex flex-col">
          {services.map((service, index) =>
            renderServiceCell(service, index, true),
          )}

          {/* Destination row */}
          <div className="flex items-center gap-3 py-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 shadow-sm flex items-center justify-center shrink-0 z-10">
              <span className="text-[10px] font-bold text-white leading-none select-none">
                ★
              </span>
            </div>
            <div className="w-11 h-11 rounded-xl bg-white shadow-[0_2px_8px_0_rgba(245,158,11,0.15)] flex items-center justify-center shrink-0">
              <img
                src={iconSuccessful}
                alt="Destination"
                className="w-7 h-7 object-contain"
              />
            </div>
            <p className="flex-1 text-sm font-bold text-amber-600">
              Destination
            </p>
            <span className="shrink-0 inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-xs font-bold text-white shadow-sm">
              Your Goal
            </span>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
          TABLET + DESKTOP  (≥ md) — horizontal grid
          ══════════════════════════════════════════════════════════ */}
      <div className="hidden md:block overflow-x-auto -mx-1 px-1 pb-1">
        <div style={{ minWidth }}>
          {/* Step columns */}
          <div className="grid" style={{ gridTemplateColumns: colTemplate }}>
            {services.map((service, index) =>
              renderServiceCell(service, index, false),
            )}

            {/* Destination column */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: services.length * 0.04,
                duration: 0.25,
                ease: 'easeOut',
              }}
              className="flex flex-col items-center gap-2 px-0.5"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-400 shadow-[0_4px_12px_0_rgba(245,158,11,0.25)] flex items-center justify-center">
                <img
                  src={iconSuccessful}
                  alt="Destination"
                  className="w-9 h-9 object-contain"
                />
              </div>
              <p className="text-[11px] font-bold text-amber-600 text-center leading-snug px-0.5 h-[30px] flex items-center justify-center">
                Destination
              </p>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-[10px] font-bold text-white shadow-sm">
                Your Goal
              </span>
            </motion.div>
          </div>

          {/* Connecting track */}
          <div className="relative mt-5 h-7">
            {/* Gradient connector line */}
            <div
              className="absolute top-1/2 h-[2px] -translate-y-1/2 rounded-full"
              style={{
                background:
                  'linear-gradient(to right, rgba(28,141,238,0.08), rgba(28,141,238,0.22), rgba(245,158,11,0.22), rgba(245,158,11,0.08))',
                left: `calc(100% / ${totalCols} / 2)`,
                right: `calc(100% / ${totalCols} / 2)`,
              }}
            />

            {/* Numbered dots */}
            <div
              className="absolute inset-0 grid"
              style={{ gridTemplateColumns: colTemplate }}
            >
              {services.map((service, index) => (
                <div
                  key={service.serviceId}
                  className="flex items-center justify-center"
                >
                  <div className="w-7 h-7 rounded-full bg-white/70 border border-[#1C8DEE]/30 shadow-sm flex items-center justify-center z-10">
                    <span className="text-[10px] font-bold text-[#1C8DEE]/70 tabular-nums leading-none select-none">
                      {index + 1}
                    </span>
                  </div>
                </div>
              ))}

              {/* Destination dot */}
              <div className="flex items-center justify-center">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 shadow-sm flex items-center justify-center z-10">
                  <span className="text-[10px] font-bold text-white leading-none select-none">
                    ★
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
